import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createTranslator } from '../../i18n/index.js';
import '../../styles/lead-pop-game.css';
import { isActivityItem } from '../activityTypes.js';
import { isMuted, toggleMuted, unlockAudio } from '../audio/sfxCore.js';
import { playResultStinger } from '../audio/gameSfx.js';
import { startGameMusic, stopGameMusic } from '../audio/gameMusic.js';
import { GAME_STATUS } from '../config.js';
import { useLeadPopGame } from '../hooks/useLeadPopGame.js';
import { GAME_INTRO, shouldDeferInitialStart } from '../systems/gameIntro.js';
import { GameHud } from './GameHud.jsx';
import { GameBoard } from './GameBoard.jsx';
import { GameResult } from './GameResult.jsx';
import { GameStartBanner } from './GameStartBanner.jsx';
import { SoundEnableHint } from './SoundEnableHint.jsx';
import { usePopBursts } from './usePopBursts.js';
import { useScoreFloaters } from './useScoreFloaters.js';

const BOARD_SHAKE_MS = 320;
const BOARD_FLASH_MS = 180;
const SOUND_HINT_MS = 4200;
const POP_REMOVE_MS = 580;

export const LeadPopGame = forwardRef(function LeadPopGame(props, ref) {
    const {
        duration = 90,
        difficulty = 'normal',
        autoStart = true,
        showStartBanner = true,
        useDurationLimit = false,
        sessionComplete = false,
        onPrimaryResultAction,
        startBannerCountdownFrom = GAME_INTRO.countdownFrom,
        startBannerCountdownIntervalMs = GAME_INTRO.countdownIntervalMs,
        startBannerGoMs = GAME_INTRO.goMs,
        paused = false,
        literals = {},
        theme = 'pastel',
        enrichmentProgress = null,
        backendLeadSignal = null,
        onReady,
        onStart,
        onScore,
        onItemHit,
        onLifeLost,
        onGameEnd,
    } = props;

    const t = useMemo(() => createTranslator(literals), [literals]);
    const [soundMuted, setSoundMuted] = useState(() => isMuted());
    const [scorePulse, setScorePulse] = useState(false);
    const [boardShake, setBoardShake] = useState(false);
    const [boardFlash, setBoardFlash] = useState(false);
    const [showSoundHint, setShowSoundHint] = useState(false);
    const [introGoPulse, setIntroGoPulse] = useState(false);

    const introPlayedRef = useRef(false);
    const soundHintShownRef = useRef(false);
    const finishStingerPlayedRef = useRef(false);
    const lastBackendSignalRef = useRef(null);
    const hostResultMode = sessionComplete && typeof onPrimaryResultAction === 'function';

    const deferInitialStart = shouldDeferInitialStart({
        autoStart,
        showStartBanner,
        introAlreadyPlayed: introPlayedRef.current,
    });

    const [introBannerActive, setIntroBannerActive] = useState(deferInitialStart);

    const triggerBoardPenaltyFx = useCallback(() => {
        setBoardShake(true);
        setBoardFlash(true);
        window.setTimeout(() => setBoardShake(false), BOARD_SHAKE_MS);
        window.setTimeout(() => setBoardFlash(false), BOARD_FLASH_MS);
    }, []);

    const { bursts, spawnBurst } = usePopBursts();
    const { floaters, spawnFloater } = useScoreFloaters();
    const itemsRef = useRef([]);

    const {
        state,
        boardRef,
        api,
        popItemAt,
        removePoppedItem,
        playItemHitSound,
        spawnBackendLead,
        useDurationLimit: timedRound,
    } = useLeadPopGame({
        duration,
        difficulty,
        autoStart,
        deferAutoStart: deferInitialStart,
        useDurationLimit,
        paused,
        enrichmentProgress,
        onBadItemHit: triggerBoardPenaltyFx,
        onReady,
        onStart,
        onScore,
        onItemHit,
        onLifeLost,
        onGameEnd,
    });

    itemsRef.current = state.items;

    const handleSoundToggle = useCallback(() => {
        const nextMuted = toggleMuted();
        setSoundMuted(nextMuted);
        if (nextMuted) {
            stopGameMusic();
        } else {
            unlockAudio();
            setShowSoundHint(false);
            if (state.status === GAME_STATUS.RUNNING && !introBannerActive) {
                startGameMusic();
            }
        }
    }, [state.status, introBannerActive]);

    useImperativeHandle(
        ref,
        () => ({
            ...api,
            completeSession: api.completeSession,
            spawnBackendLead,
        }),
        [api, spawnBackendLead],
    );

    useEffect(() => {
        if (!sessionComplete) return;
        if (state.status === GAME_STATUS.RUNNING) {
            api.completeSession();
        }
    }, [sessionComplete, state.status, api]);

    useEffect(() => {
        const row = backendLeadSignal?.row ?? backendLeadSignal;
        const signalKey = backendLeadSignal?.key;
        if (!row) return;
        if (signalKey != null && lastBackendSignalRef.current === signalKey) return;
        if (signalKey != null) lastBackendSignalRef.current = signalKey;
        spawnBackendLead(row);
    }, [backendLeadSignal, spawnBackendLead]);

    useEffect(() => {
        const shouldPlayMusic =
            state.status === GAME_STATUS.RUNNING && !soundMuted && !introBannerActive;

        if (shouldPlayMusic) {
            startGameMusic();
            return () => stopGameMusic();
        }

        stopGameMusic();
        return undefined;
    }, [state.status, soundMuted, introBannerActive]);

    useEffect(() => {
        if (state.status !== GAME_STATUS.FINISHED || finishStingerPlayedRef.current) return;
        finishStingerPlayedRef.current = true;
        stopGameMusic();
        playResultStinger(state.outcome);
    }, [state.status, state.outcome]);

    useEffect(() => {
        if (!showSoundHint) return undefined;
        const timerId = window.setTimeout(() => setShowSoundHint(false), SOUND_HINT_MS);
        return () => window.clearTimeout(timerId);
    }, [showSoundHint]);

    const handleIntroComplete = useCallback(() => {
        introPlayedRef.current = true;
        setIntroBannerActive(false);
        setIntroGoPulse(true);
        window.setTimeout(() => setIntroGoPulse(false), 900);
        api.start();
    }, [api]);

    useEffect(() => {
        if (paused) api.pause();
    }, [paused, api]);

    const handlePrimaryResultAction = useCallback(() => {
        if (hostResultMode) {
            onPrimaryResultAction();
            return;
        }
        finishStingerPlayedRef.current = false;
        api.start();
    }, [api, hostResultMode, onPrimaryResultAction]);

    const primaryResultLabelKey = hostResultMode
        ? 'result.viewProspectingResults'
        : 'result.restart';

    const hideBoardHint = introBannerActive;

    const handleItemClick = useCallback(
        (itemId) => {
            const item = itemsRef.current.find((entry) => entry.id === itemId);
            if (!item || item.popping) return;

            playItemHitSound(item);
            popItemAt(item);
            spawnBurst(item);
            spawnFloater(item);

            window.setTimeout(() => {
                removePoppedItem(itemId);
            }, POP_REMOVE_MS);

            if (item.points > 0) {
                setScorePulse(true);
                window.setTimeout(() => setScorePulse(false), 160);

                if (
                    soundMuted &&
                    !soundHintShownRef.current &&
                    (isActivityItem(item) || item.fromBackend)
                ) {
                    soundHintShownRef.current = true;
                    setShowSoundHint(true);
                }
            }
        },
        [playItemHitSound, popItemAt, removePoppedItem, spawnBurst, spawnFloater, soundMuted],
    );

    return (
        <div className={`lp-game lp-game--${theme}`} data-status={state.status}>
            <GameHud
                state={state}
                t={t}
                soundMuted={soundMuted}
                onSoundToggle={handleSoundToggle}
                showTimer={timedRound}
                scorePulse={scorePulse}
            />

            <GameBoard
                ref={boardRef}
                state={state}
                t={t}
                bursts={bursts}
                floaters={floaters}
                onItemClick={handleItemClick}
                hideHint={hideBoardHint}
                boardShake={boardShake}
                boardFlash={boardFlash}
                introGoPulse={introGoPulse}
            />

            <SoundEnableHint
                visible={showSoundHint}
                message={t('game.soundHint')}
                onEnable={handleSoundToggle}
            />

            <GameStartBanner
                t={t}
                active={introBannerActive}
                countdownFrom={startBannerCountdownFrom}
                countdownIntervalMs={startBannerCountdownIntervalMs}
                goMs={startBannerGoMs}
                onComplete={handleIntroComplete}
            />

            {state.status === GAME_STATUS.PAUSED && (
                <div className="lp-game__paused" role="status">
                    {t('game.paused')}
                </div>
            )}

            {state.status === GAME_STATUS.FINISHED && (
                <GameResult
                    state={state}
                    t={t}
                    onPrimaryAction={handlePrimaryResultAction}
                    primaryLabelKey={primaryResultLabelKey}
                    showConfetti
                />
            )}
        </div>
    );
});
